import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser } from '@fortawesome/free-solid-svg-icons';
import { createModalEscapeKeyDownHandler } from '../../../../utils/modalEscape';
import { formatPlanDateTime } from './rpmCarePlanUtils';
import './RpmCarePlan.css';

const RpmCarePlanLogModal = ({ isOpen, onClose, plan, patientName }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || !plan) return null;

  const history = Array.isArray(plan.history) ? [...plan.history].reverse() : [];

  return createPortal(
    <div
      className="rcp-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={createModalEscapeKeyDownHandler(onClose)}
      role="presentation"
    >
      <div className="rcp-modal rcp-modal--log" role="dialog" aria-modal="true" aria-labelledby="rcpLogTitle">
        <div className="rcp-head">
          <div>
            <p className="rcp-head-kicker">Audit trail</p>
            <h2 id="rcpLogTitle">{plan.planTitle}</h2>
          </div>
          <div className="rcp-head-who">
            <b>{patientName}</b><br />
            {history.length} event{history.length === 1 ? '' : 's'} recorded
          </div>
          <button type="button" className="rcp-close" aria-label="Close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="rcp-scroll">
          {history.length === 0 ? (
            <p className="rcp-log-empty">No history recorded yet.</p>
          ) : (
            history.map((entry, idx) => (
              <div key={`${entry.at}-${idx}`} className="rcp-log-entry">
                <div className="rcp-log-top">
                  <span className="rcp-log-when">{formatPlanDateTime(entry.at)}</span>
                  <span className="rcp-log-by">
                    <FontAwesomeIcon icon={faUser} />
                    <b>{entry.byName || entry.by || 'unknown'}</b>
                    {entry.byRole ? <span className="rcp-log-role">{entry.byRole}</span> : null}
                  </span>
                  <span className={`rcp-log-badge ${entry.action === 'created' ? 'created' : 'updated'}`}>
                    {entry.action === 'created' ? 'Created' : 'Updated'}
                  </span>
                </div>
                {entry.action === 'created' ? (
                  <p className="rcp-log-note">Care plan created by {entry.byName || entry.by || 'unknown'}.</p>
                ) : !entry.changes?.length ? (
                  <p className="rcp-log-note">Saved with no field changes.</p>
                ) : (
                  <table className="rcp-logtbl">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Previous</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.changes.map((c, i) => (
                        <tr key={`${c.field}-${i}`}>
                          <td className="f">{c.field}</td>
                          <td className="old">{c.from}</td>
                          <td className="new">{c.to}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}
        </div>
        <div className="rcp-foot">
          <span className="rcp-status">Every change is recorded with date, time, and user.</span>
          <button type="button" className="rcp-btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RpmCarePlanLogModal;
